import { Modal, DropZone, Button, Text } from "@medentic/polaris-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useUser } from "@shared/hooks/user/useUser";
import { showToast } from "@shared/lib";

interface Props {
  open: boolean;
  onClose: () => void;
  csvExampleUrl: string;
}

export default function AttendeesImportExportModal({
  open,
  onClose,
  csvExampleUrl,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [file, setFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { GET, POST } = useApiRequest();
  const queryClient = useQueryClient();
  const { user } = useUser(true);

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return POST(
        `/api/healthcare-companies/${hcc}/e/attendees/import`,
        formData
      );
    },
    onSuccess: () => {
      // Invalidate attendees query to refresh the list
      setFile(null);

      // Show success toast with user's email
      dispatch(
        showToast({
          message: t("attendees.importModal.successMessage", {
            email: user?.email || "your email",
          }),
          type: "success",
        })
      );
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["attendees"] });
      }, 2000);

      onClose();
    },
  });

  const handleDownloadExample = async () => {
    try {
      setIsDownloading(true);
      const result = await GET<{ url: string }>(csvExampleUrl);

      const response = await fetch(result.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attendees-example.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download example file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDrop = (_dropFiles: File[], acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const droppedFile = acceptedFiles[0];
      setFile(droppedFile);
    }
  };

  const handleImportClick = () => {
    if (file) {
      importMutation.mutate(file);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("attendees.importModal.title")}
      primaryAction={{
        content: t("attendees.importModal.import"),
        onAction: handleImportClick,
        disabled: !file || importMutation.isPending,
        loading: importMutation.isPending,
      }}
      secondaryActions={[{ content: t("common.cancel"), onAction: onClose }]}
    >
      <Modal.Section>
        <DropZone
          accept="text/csv"
          type="file"
          onDrop={handleDrop}
          allowMultiple={false}
          disabled={importMutation.isPending}
        >
          <DropZone.FileUpload
            actionTitle={t("attendees.importModal.dropZoneAction")}
          />
        </DropZone>

        {file && (
          <div style={{ marginTop: 16 }}>
            <Text as="p" variant="bodyMd">
              {t("attendees.importModal.selectedFile") || "Selected file"}:{" "}
              {file.name}
            </Text>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Button
            onClick={handleDownloadExample}
            loading={isDownloading}
            variant="plain"
          >
            {t("attendees.importModal.downloadExample")}
          </Button>
        </div>
      </Modal.Section>
    </Modal>
  );
}
